const OUTCOME_FLAGS = [
  'single_item_order',
  'scan_tote_to_complete',
  'put_in_tote',
  'assigned_to_tote',
  'pending_order'
];

export function resolveOutcome(res) {
  if (!res) return 'notice';
  return OUTCOME_FLAGS.find(flag => res[flag]) || 'notice';
}

function normalizeMessage(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(' ');
  return value || '';
}

export function getNoticeMessage(res) {
  return (
    normalizeMessage(res?.notice_messages) ||
    normalizeMessage(res?.error_messages) ||
    ''
  );
}

export function isCorrectTote(input, tote) {
  return (input || '').trim().toLowerCase() === (tote?.name || '').toLowerCase();
}

export function isOrderCompleteResponse(res) {
  return res?.scan_tote_to_completed === true;
}

function revertPending(item) {
  if (!item) return null;
  return {
    ...item,
    scanned_qty: (item.scanned_qty || 0) - 1,
    qty_remaining: (item.qty_remaining || 0) + 1
  };
}

function resolvePendingType(outcome, res) {
  if (outcome !== 'pending_order') return outcome;
  return res?.can_complete_order ? 'scan_tote_to_complete' : 'put_in_tote';
}

export function mapProductScanResult(res) {
  const outcome = resolveOutcome(res);

  if (outcome === 'notice') {
    return {outcome, notice: getNoticeMessage(res)};
  }

  if (outcome === 'single_item_order') {
    return {
      outcome,
      order: res.order,
      suggestedItem: res.order_item,
      itemsInTote: res.order_items_scanned || [],
      unscannedItems: [],
      tote: null,
      pendingScan: null,
      orderComplete: true,
      completeMessage: res.single_item_order_message || '',
      completeResetSeconds: res.single_item_order_message_time,
      stage: 'product'
    };
  }

  const currentId = res.order_item?.id;
  const scanned = res.order_items_scanned || [];
  const partial = res.order_items_partial_scanned || [];
  const unscanned = res.order_items_unscanned || [];
  const fullList = res.unscanned_items || [];

  const currentEntry = [...scanned, ...partial].find(
    item => item.order_item_id === currentId
  );

  let suggestedItem = fullList.find(item => item.order_item_id === currentId);
  if (!suggestedItem) {
    suggestedItem = revertPending(currentEntry);
  }

  const placedQty = (currentEntry?.scanned_qty || 0) - 1;
  const itemsInTote = scanned.filter(item => item.order_item_id !== currentId);
  if (placedQty > 0) {
    itemsInTote.unshift({...currentEntry, scanned_qty: placedQty});
  }

  const unscannedItems = fullList.length
    ? fullList
    : [
        ...unscanned,
        ...partial.filter(item => item.order_item_id !== currentId),
        suggestedItem
      ].filter(Boolean);

  const pendingType = resolvePendingType(outcome, res);
  const bannerState =
    pendingType === 'scan_tote_to_complete'
      ? 'collect'
      : outcome === 'put_in_tote' || itemsInTote.length > 0
      ? 'occupied'
      : 'empty';

  return {
    outcome,
    order: res.order,
    tote: res.tote,
    bannerState,
    suggestedItem,
    itemsInTote,
    unscannedItems,
    pendingScan: {
      type: pendingType,
      orderItemId: currentId,
      tote: res.tote,
      barcodeInput: res.barcode_input
    },
    orderComplete: false,
    stage: 'tote'
  };
}

export function applyToteConfirmation(suggestedItem, itemsInTote, unscannedItems) {
  if (!suggestedItem) return {itemsInTote, unscannedItems, suggestedItem: null};

  const id = suggestedItem.order_item_id;
  const placed = {
    ...suggestedItem,
    scanned_qty: (suggestedItem.scanned_qty || 0) + 1,
    qty_remaining: (suggestedItem.qty_remaining || 0) - 1
  };

  const alreadyInTote = itemsInTote.some(item => item.order_item_id === id);
  const nextItemsInTote = alreadyInTote
    ? itemsInTote.map(item =>
        item.order_item_id === id
          ? {...item, scanned_qty: (item.scanned_qty || 0) + 1}
          : item
      )
    : [placed, ...itemsInTote];

  const nextUnscanned = unscannedItems
    .map(item => (item.order_item_id === id ? placed : item))
    .filter(item => (item.qty_remaining || 0) > 0);

  return {
    itemsInTote: nextItemsInTote,
    unscannedItems: nextUnscanned,
    suggestedItem: placed.qty_remaining > 0 ? placed : null
  };
}

export function buildToteScanPayload(pendingScan, toteBarcode) {
  return {
    type: pendingScan.type,
    tote: pendingScan.tote,
    order_item_id: pendingScan.orderItemId,
    tote_barcode: toteBarcode,
    barcode_input: pendingScan.barcodeInput
  };
}
